import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription, timer } from 'rxjs';

import { AuthService } from '../../../../core/auth/auth.service';
import { aiConsentBoxNeeded } from '../../../../core/attestations/ai-consent.types';
import { PreApplicationService } from '../../../../core/applications/pre-application.service';
import {
  PreApplication,
  PreApplicationDocType,
} from '../../../../core/applications/pre-application.types';
import { AiConsentCheckboxComponent } from '../../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import {
  DocDropComponent,
  DocDropType,
  DocUpload,
  uploadErrorMessage,
} from '../../shared/doc-drop/doc-drop';
import { readError } from '../../../../core/http/read-error';

/** The three buckets a client may add to, in the wizard's order. */
const DOC_BLOCKS: { type: DocDropType<PreApplicationDocType>; why: string }[] = [
  {
    type: {
      value: 'BANK_STATEMENT',
      label: 'Bank statements',
      hint: '6–9 months — the more you share, the better',
    },
    why: 'Shows your cash flow — the basis for every funding decision.',
  },
  {
    type: {
      value: 'AGE_ANALYSIS',
      label: 'Age analysis',
      hint: 'Your current list of who owes you and for how long',
    },
    why: 'Lets us consider you for Debtors Finance.',
  },
  {
    type: {
      value: 'FINANCIALS',
      label: 'Financial statements',
      hint: 'Profit & loss, balance sheet, cash flow statements',
    },
    why: 'Strengthens your application and can improve your offer.',
  },
];

/**
 * Add documents to an enquiry that is already under review.
 *
 * PO ruling 2026-09-22: a SUBMITTED pre-application accepts uploads — optional,
 * add-only. The rules already contemplate financial statements arriving after
 * the original submission (Constitution §A2.4), and Ops sees the late arrival
 * through the audit event and the worklist count. Nothing here re-submits or
 * decides: the enquiry stays under review with more evidence on it.
 *
 * Its own page rather than a mode of the wizard: the wizard reopens only an
 * editable intake (so a client can always start afresh), and this page opens
 * a SUBMITTED one by short_id. It reuses the same drop blocks and the same
 * upload endpoint, and reads the verdicts back by a plain poll.
 *
 * No amount, no size element (UI-DA-58-01). Removal is not offered: what Ops
 * may already have read is not pulled out from under the review.
 */
@Component({
  selector: 'app-add-documents',
  imports: [
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    IconComponent,
    StatusChipComponent,
    DocDropComponent,
    AiConsentCheckboxComponent,
  ],
  templateUrl: './add-documents.html',
  styleUrl: './add-documents.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDocumentsComponent implements OnInit {
  private static readonly POLL_MS = 3000;
  private static readonly POLL_MAX_ATTEMPTS = 20;

  private readonly preApp = inject(PreApplicationService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly preAppId = this.route.snapshot.paramMap.get('preAppId') ?? '';

  protected readonly DOC_BLOCKS = DOC_BLOCKS;

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly record = signal<PreApplication | null>(null);
  protected readonly uploads = signal<DocUpload<PreApplicationDocType>[]>([]);
  protected readonly serverError = signal<string | null>(null);

  /** The enquiry takes documents only while it accepts them (see the type). */
  protected readonly accepting = computed(() => this.record()?.accepts_documents === true);

  /** How many of the documents on screen were added after submission. */
  protected readonly addedCount = computed(
    () => this.record()?.post_submission_document_count ?? 0,
  );

  // --- AI-processing consent: the same gate the wizard's upload has. ---
  protected readonly aiConsent = signal(false);
  // The business's consent since FLOW-297 — `ai_consent` is its state.
  protected readonly consentOnRecord = computed(
    () =>
      this.record()?.ai_processing_consent === true ||
      !aiConsentBoxNeeded(this.record()?.ai_consent, this.auth.hasStandingAiConsent()),
  );
  protected readonly showAiConsentBox = computed(
    () => this.record() !== null && !this.consentOnRecord(),
  );
  protected readonly consentBlocked = signal(false);

  protected readonly uploadsByType = computed(() => {
    const map = new Map<PreApplicationDocType, DocUpload<PreApplicationDocType>[]>();
    for (const u of this.uploads()) {
      map.set(u.docType, [...(map.get(u.docType) ?? []), u]);
    }
    return map;
  });

  protected readonly anyInFlight = computed(() =>
    this.uploads().some((u) => u.state === 'uploading'),
  );

  private pollSub: Subscription | null = null;
  private pollAttempts = 0;

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.preApp
      .get(this.preAppId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pa) => {
          this.hydrate(pa);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private hydrate(pa: PreApplication): void {
    this.record.set(pa);
    this.uploads.set(
      pa.documents.map((d, i) => ({
        id: `${d.doc_type}-${d.short_id}-${i}`,
        docType: d.doc_type,
        fileName: d.file_name,
        state: 'done' as const,
        doc: {
          short_id: d.short_id,
          file_name: d.file_name,
          assess_status: d.assess_status,
          assess_message: d.assess_message,
        },
      })),
    );
  }

  protected onAiConsentChange(checked: boolean): void {
    this.aiConsent.set(checked);
    if (checked) this.consentBlocked.set(false);
  }

  protected onFilesPicked(docType: PreApplicationDocType, files: File[]): void {
    if (!this.accepting()) return;
    if (!this.consentOnRecord() && !this.aiConsent()) {
      this.consentBlocked.set(true);
      return;
    }
    this.serverError.set(null);
    const start = () => {
      for (const file of files) this.uploadOne(docType, file);
    };
    if (this.consentOnRecord()) {
      start();
      return;
    }
    // The box was ticked here: record it on the enquiry first, or every upload
    // would be refused with a ticked box on screen (same as the wizard).
    this.preApp
      .recordAiConsent(this.preAppId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pa) => {
          this.record.set(pa);
          this.auth.markAiConsentGiven();
          start();
        },
        error: (err) => this.serverError.set(readError(err)),
      });
  }

  private uploadOne(docType: PreApplicationDocType, file: File): void {
    const id = `${docType}-${file.name}-${file.size}-${this.uploads().length}`;
    this.uploads.update((list) => [
      ...list,
      { id, docType, fileName: file.name, state: 'uploading', doc: null },
    ]);
    this.preApp
      .uploadDocument(this.preAppId, file, docType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (doc) => {
          this.patchUpload(id, {
            state: 'done',
            doc: {
              short_id: doc.short_id,
              file_name: doc.file_name,
              assess_status: doc.assess_status,
              assess_message: doc.assess_message,
            },
          });
          this.record.update((r) =>
            r ? { ...r, post_submission_document_count: r.post_submission_document_count + 1 } : r,
          );
          this.ensurePolling();
        },
        error: (err) => this.patchUpload(id, { state: 'error', error: uploadErrorMessage(err) }),
      });
  }

  /** Removal is not offered once submitted; the drop block's remove only drops a failed row. */
  protected removeUpload(uploadId: string): void {
    this.uploads.update((list) => list.filter((u) => u.id !== uploadId || u.doc !== null));
  }

  private patchUpload(id: string, patch: Partial<DocUpload<PreApplicationDocType>>): void {
    this.uploads.update((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    this.cdr.markForCheck();
  }

  private hasPendingVerdict(): boolean {
    return this.uploads().some(
      (u) => u.state === 'done' && (u.doc?.assess_status ?? 'PENDING') === 'PENDING',
    );
  }

  /** Read verdicts back by a plain poll until none is pending (or we give up). */
  private ensurePolling(): void {
    if (this.pollSub) return;
    this.pollAttempts = 0;
    this.pollSub = timer(AddDocumentsComponent.POLL_MS, AddDocumentsComponent.POLL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.pollOnce());
  }

  private pollOnce(): void {
    if (
      !this.hasPendingVerdict() ||
      ++this.pollAttempts > AddDocumentsComponent.POLL_MAX_ATTEMPTS
    ) {
      this.pollSub?.unsubscribe();
      this.pollSub = null;
      return;
    }
    this.preApp
      .get(this.preAppId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pa) => {
        for (const d of pa.documents) {
          this.uploads.update((list) =>
            list.map((u) =>
              u.doc?.short_id === d.short_id
                ? {
                    ...u,
                    doc: {
                      ...u.doc,
                      assess_status: d.assess_status,
                      assess_message: d.assess_message,
                    },
                  }
                : u,
            ),
          );
        }
        this.cdr.markForCheck();
      });
  }

  protected done(): void {
    void this.router.navigate([
      '/entity',
      this.entityId,
      'applications',
      'submission',
      this.preAppId,
    ]);
  }
}
